import { useCallback } from 'react'

import { track } from '@/shared/analytics/analyticsService'

export interface PlaylistTrackingData {
	playlistId: number
	playlistTitle?: string
}

export interface SongTrackingData {
	songId: number
	songTitle?: string | null
	artistName?: string | null
}

export type PlaylistEventType =
	| 'playlist_play_now'
	| 'playlist_song_search'
	| 'playlist_song_play'
	| 'playlist_song_autoplay'

/**
 * Hook for tracking playlist-related events.
 */
export const usePlaylistTracking = () => {
	/**
	 * Track when user clicks the "Play Now" button in playlist header
	 */
	const trackPlayNow = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_play_now', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user searches for a song in the playlist
	 */
	const trackSongSearch = useCallback(
		(playlist: PlaylistTrackingData, searchQuery: string) => {
			track('playlist_song_search', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				search_query: searchQuery,
			})
		},
		[]
	)

	/**
	 * Track when user clicks to play a specific song from the list
	 */
	const trackSongPlay = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_song_play', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	/**
	 * Track when a song starts playing automatically after the previous one ends
	 */
	const trackSongAutoplay = useCallback(
		(
			playlist: PlaylistTrackingData,
			previousSong: SongTrackingData,
			nextSong: SongTrackingData
		) => {
			track('playlist_song_autoplay', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				previous_song_id: previousSong.songId,
				previous_song_title: previousSong.songTitle,
				previous_artist_name: previousSong.artistName,
				next_song_id: nextSong.songId,
				next_song_title: nextSong.songTitle,
				next_artist_name: nextSong.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user clicks the "Next" button to skip to the next song
	 */
	const trackNextSong = useCallback(
		(
			playlist: PlaylistTrackingData,
			currentSong: SongTrackingData,
			nextSong: SongTrackingData
		) => {
			track('playlist_song_next', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				current_song_id: currentSong.songId,
				current_song_title: currentSong.songTitle,
				current_artist_name: currentSong.artistName,
				next_song_id: nextSong.songId,
				next_song_title: nextSong.songTitle,
				next_artist_name: nextSong.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user clicks the "Previous" button to go back to the previous song
	 */
	const trackPreviousSong = useCallback(
		(
			playlist: PlaylistTrackingData,
			currentSong: SongTrackingData,
			previousSong: SongTrackingData
		) => {
			track('playlist_song_previous', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				current_song_id: currentSong.songId,
				current_song_title: currentSong.songTitle,
				current_artist_name: currentSong.artistName,
				previous_song_id: previousSong.songId,
				previous_song_title: previousSong.songTitle,
				previous_artist_name: previousSong.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user pauses the current song
	 */
	const trackSongPause = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_song_pause', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user resumes playing a paused song
	 */
	const trackSongResume = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_song_resume', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user gives positive feedback (like) to a song
	 */
	const trackFeedbackPositive = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_feedback_positive', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	/**
	 * Track when user gives negative feedback (dislike) to a song
	 */
	const trackFeedbackNegative = useCallback(
		(
			playlist: PlaylistTrackingData,
			song: SongTrackingData,
			feedbackReason?: string
		) => {
			track('playlist_feedback_negative', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
				feedback_reason: feedbackReason,
			})
		},
		[]
	)

	/**
	 * Track when user closes the player
	 */
	const trackPlayerClose = useCallback(
		(playlist: PlaylistTrackingData, song: SongTrackingData) => {
			track('playlist_player_close', {
				playlist_id: playlist.playlistId,
				playlist_title: playlist.playlistTitle,
				song_id: song.songId,
				song_title: song.songTitle,
				artist_name: song.artistName,
			})
		},
		[]
	)

	return {
		trackPlayNow,
		trackSongSearch,
		trackSongPlay,
		trackSongAutoplay,
		trackNextSong,
		trackPreviousSong,
		trackSongPause,
		trackSongResume,
		trackFeedbackPositive,
		trackFeedbackNegative,
		trackPlayerClose,
	}
}
